'use client'

import { Builder, BuilderBlocks } from "@builder.io/react";
import { BuilderBlock } from '@builder.io/react/dist/types/src/components/builder-block.component';
import { Component as ComponentObj } from '@builder.io/sdk';
import CloseIcon from 'components/icons/close';
import { ReactNode, useState } from 'react';
import ReactModal from 'react-modal';
import YouTube, { YouTubePlayer, YouTubeProps } from 'react-youtube';
import './video-timer.css';

interface videoTimerProps {
  videoSettings: {
    videoService: string;
    videoId: string;
    thumbnailImg: string;
    autoplay: boolean;
    controls: boolean;
    loop: boolean;
    mute: boolean;
    inline: boolean;
  };
  displaySettings: {
    fit: 'cover' | 'contain';
    position: string;
    aspectRatio: string;
  };
  builderBlock?: BuilderBlock;
  timerSettings: {
    title: string;
    minutes: number;
    seconds: number;
    enabled: boolean;
    content: ReactNode;
  };
}


export const VideoTimer = (
  props: videoTimerProps
) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isThumbnailShown, setIsThumbnailShown] = useState(props.videoSettings.thumbnailImg && !props.videoSettings.autoplay); // default true if thumbnail image exists, otherwise false

  const isEditing = Builder.isEditing;
  const timerEnabled = props.videoSettings.inline && props.timerSettings.enabled;
  let videoTime = 0;
  let timeUpdater: any;
  let globalPlayer: YouTubePlayer;
  const timerSeconds = (props.timerSettings.minutes * 60) + props.timerSettings.seconds;
  function onProgress(currentTime: any, player: YouTubePlayer) {
    if (Math.floor(currentTime) === timerSeconds || (props.videoSettings.loop && (timerSeconds >= player.getDuration()) && (Math.ceil(currentTime) + 1 >= player.getDuration()))) {
      // open modal here
      player.pauseVideo();
      clearInterval(timeUpdater);
      setIsModalOpen(true);
    }
  }
  const onPlayerReady: YouTubeProps['onReady'] = (event) => {
    // access to player in all event handlers via event.target
    const player = event.target;
    globalPlayer = player;

    if (props.videoSettings.mute) {
      player.mute();
    }

    if (props.videoSettings.autoplay && timerEnabled) {
      setTimeout(() => {
        // wait 100 ms to determine if video actually started autoplaying and wasn't blocked
        // when it autplays it doesn't emit an onStateChange event 
        if (player.getCurrentTime() > 0) {
          timeUpdater = setInterval(() => {
            const oldTime = videoTime;
            if (player && player.getCurrentTime()) {
              videoTime = player.getCurrentTime();
            }
            if (videoTime !== oldTime) {
              onProgress(videoTime, player);
            }
          }, 1000);
        } else {
          clearInterval(timeUpdater);
        }
      }, 100);
    } else {
      clearInterval(timeUpdater);
    }
  }

  const onVideoEnd: YouTubeProps['onEnd'] = (event) => {
    const player = event.target;
    if (timerEnabled && timerSeconds >= player.getDuration()) {
      // if timer length is greater than video length, display modal here (once video ends)
      setIsModalOpen(true);
      clearInterval(timeUpdater);
    }
  }

  const onVideoStateChange: YouTubeProps['onStateChange'] = (event) => {
    // access to player in all event handlers via event.target
    const player = event.target;
    globalPlayer = player;
    function updateTime() {
      const oldTime = videoTime;
      if (player && player.getCurrentTime()) {
        videoTime = player.getCurrentTime();
      }
      if (videoTime !== oldTime) {
        onProgress(videoTime, player);
      }
    }
    if (timerEnabled) {
      // see onStateChange documentation for integer values: https://developers.google.com/youtube/iframe_api_reference#Events
      // playing
      if (event.data === 1) {
        // start/resume the timer if the user plays the video manually...
        timeUpdater = setInterval(updateTime, 1000);
      } else {
        // ...stop the timer if the user stops/pauses the video or if it buffers
        clearInterval(timeUpdater);
      }
    } else {
      clearInterval(timeUpdater);
    }
  }

  const opts: YouTubeProps['opts'] = {
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      autoplay: props.videoSettings.autoplay || !!props.videoSettings.thumbnailImg, // we want the player to autoplay once it's rendered if we have a thumbnail image, so autoplay true if thumbnail image is used
      controls: props.videoSettings.controls ? 1 : 0,
      disablekb: !props.videoSettings.controls ? 1 : 0,
      loop: props.videoSettings.loop ? 1 : 0,
      playsinline: props.videoSettings.inline ? 1 : 0,
      enablejsapi: 1,
      rel: 0, // don't display videos from other youtube channels (will still display videos from the same youtube channel, nothing we can do about that)
    },
  };

  if (props.videoSettings.loop) {
    opts.playerVars.playlist = props.videoSettings.videoId;
  }

  const closeModal = () => {
    setIsModalOpen(false);
    globalPlayer.playVideo();
    clearInterval(timeUpdater);
  }

  if (isEditing) {
    return (
      <>
        <div className="video-timer-thumbnail-wrapper">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={props.videoSettings.thumbnailImg}
            className="video-block-poster-image"
            alt="Video thumbnail"
            style={{
              objectFit: props.displaySettings.fit ?? 'cover',
              objectPosition: props.displaySettings.position ?? 'center',
              width: '100%',
              aspectRatio: props.displaySettings.aspectRatio
            }}
          />
        </div>
        <div style={{
            border: '3px solid #F4F4F4',
            padding: '10px'
        }}>
            <p>Modal Header {props.builderBlock?.id}</p>
            <BuilderBlocks 
            parentElementId={props.builderBlock?.id}
            dataPath="component.options.timerSettings.content"
            blocks={props.timerSettings.content}
            />
        </div>
      </>
    )
  }

  if (isThumbnailShown) {
    return (
        <div className="video-timer-thumbnail-wrapper" onClick={() => setIsThumbnailShown(false)}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={props.videoSettings.thumbnailImg}
            className="video-block-poster-image"
            alt="Video thumbnail"
            style={{
              objectFit: props.displaySettings.fit ?? 'cover',
              objectPosition: props.displaySettings.position ?? 'center',
              width: '100%',
              aspectRatio: props.displaySettings.aspectRatio
            }}
          />
        </div>
    );
  }

  return (
    <>
      <div className="youtube-component-wrapper" style={{aspectRatio: props.displaySettings.aspectRatio, height: '100%', width: '100%'}}>
        <YouTube videoId={props.videoSettings.videoId} opts={opts} onReady={onPlayerReady} onEnd={onVideoEnd} onStateChange={onVideoStateChange}/>
      </div>
      <ReactModal
            isOpen={isModalOpen}
            ariaHideApp={false}
            bodyOpenClassName="has-activeModal"
            onRequestClose={closeModal}
            overlayClassName="modal-backdrop"
            className="modal-dialog"
          >
            <header className="modal-header">
              {props.timerSettings.title && 
                <span className="modal-header-text">{props.timerSettings.title}</span>
              }

              <button
                className="modal-close"
                data-test="modal-close-button"
                onClick={closeModal}
              >
                <CloseIcon />
              </button>
            </header>

            <div className="modal-content" data-test="modal-content">
              <BuilderBlocks 
                parentElementId={props.builderBlock?.id}
                dataPath="component.options.timerSettings.content"
                blocks={props.timerSettings.content}
              />
            </div>
          </ReactModal>
    </>
  );
};

export const videoTimerOptions: ComponentObj = {
  name: 'Video Timer',
  friendlyName: 'Video Timer',
  inputs: [
    {
      name: 'videoSettings',
      type: 'object',
      defaultValue: {
        videoService: 'YouTube',
        thumbnailImg: 'https://cdn.builder.io/api/v1/image/assets%2F1adbd5971dac4273891f273603729777%2F150cebebb11f471995d81c043686e357',
        autoplay: true,
        controls: false,
        loop: false,
        mute: false,
        inline: false
      },
      subFields: [
        {
          name: 'videoService',
          type: 'string',
          enum: ['YouTube', 'otherService'],
          required: true
        },
        {
          name: 'videoId',
          type: 'string',
          required: true
        },
        {
          name: 'thumbnailImg',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: false
        },
        {
          name: 'autoplay',
          type: 'boolean',
        },
        {
          name: 'controls',
          type: 'boolean',
        },
        {
          name: 'loop',
          type: 'boolean',
        },
        {
          name: 'mute',
          type: 'boolean',
        },
        {
          name: 'inline',
          type: 'boolean',
        }
      ]
    },
    {
      name: 'displaySettings',
      type: 'object',
      defaultValue: {
        fit: 'cover',
        position: 'center',
        aspectRatio: '16/9'
      },
      subFields: [
        {
          name: 'fit',
          type: 'string',
          enum: ['cover', 'contain']
        },
        {
          name: 'position',
          type: 'string',
          enum: ['center', 'top', 'bottom']
        },
        {
          name: 'aspectRatio',
          type: 'string',
          enum: ['1/1', '4/3', '16/9']
        }
      ]
    },
    {
      name: 'timerSettings',
      type: 'object',
      defaultValue: {
        enabled: false,
        minutes: 0,
        seconds: 0,
        content: []
      },
      subFields: [
        {
          name: 'enabled',
          type: 'boolean',
        },
        {
          name: 'title',
          type: 'string'
        },
        {
          name: 'minutes',
          type: 'number'
        },
        {
          name: 'seconds',
          type: 'number'
        },
        {
          name: 'content',
          type: 'uiBlocks',
        }
      ]
    }
  ]
}
